import React, { useState } from "react";
import {
  Plus,
  Play,
  Trash2,
  MoreHorizontal,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import SectionHeader from "@/components/v2/SectionHeader";
import EmptyState from "@/components/v2/EmptyState";
import TableSkeleton from "@/components/v2/TableSkeleton";
import ConfirmDialog from "@/components/v2/ConfirmDialog";
import TestResultDialog from "@/components/v2/TestResultDialog";
import PortfolioRuleSheet from "@/components/v2/PortfolioRuleSheet";
import usePortfolioRules from "@/components/v2/hooks/usePortfolioRules";

export default function PortfolioRulesTab() {
  const { rules, loading, error, refetch, createRule, updateRule, deleteRule, toggleRule, testRule } = usePortfolioRules();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testResultOpen, setTestResultOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const handleCreate = async (data) => createRule(data);
  const handleUpdate = async (data) => {
    if (!editingRule) return false;
    return await updateRule(editingRule.id, data);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    await deleteRule(deleteConfirm.id);
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleTest = async (rule) => {
    setTestingId(rule.id);
    try {
      const result = await testRule(rule.id);
      setTestResult(result);
      setTestResultOpen(true);
    } catch (e) {
      // error handled in hook
    } finally {
      setTestingId(null);
    }
  };

  const slPercent = (rule) => {
    if (!rule.stop_loss?.enabled) return null;
    return rule.stop_loss.trigger_percent;
  };

  const tpPercent = (rule) => {
    if (!rule.take_profit?.enabled) return null;
    return rule.take_profit.trigger_percent;
  };

  if (error) {
    return (
      <div className="bg-surface-2 border border-border rounded-sm p-6">
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-loss" />
          <p className="text-sm text-muted-foreground">Failed to load portfolio rules</p>
          <Button size="sm" variant="outline" onClick={refetch} className="rounded-sm border-border">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 border border-border rounded-sm p-4">
      <SectionHeader
        title="Portfolio Rules"
        description="Configure portfolio-level risk management rules"
        actionLabel="Add Rule"
        actionIcon={Plus}
        onAction={() => { setEditingRule(null); setSheetOpen(true); }}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : rules.length === 0 ? (
        <EmptyState text="No portfolio rules yet. Create one to automate portfolio risk management." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Stop Loss</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Take Profit</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Interval</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Enabled</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Last Checked</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule.id} className="border-border">
                <TableCell className="font-medium text-sm">{rule.name}</TableCell>
                <TableCell>
                  {slPercent(rule) != null ? (
                    <Badge variant="outline" className="text-[10px] font-mono border-border text-loss">
                      {slPercent(rule)}%
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {tpPercent(rule) != null ? (
                    <Badge variant="outline" className="text-[10px] font-mono border-border text-profit">
                      +{tpPercent(rule)}%
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs text-muted-foreground">{rule.check_interval}m</span>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={() => toggleRule(rule.id)}
                  />
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs text-muted-foreground">
                    {rule.last_checked ? format(new Date(rule.last_checked), "dd MMM HH:mm") : "Never"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm">
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-surface-2 border-border rounded-sm min-w-[130px]">
                      <DropdownMenuItem
                        className="text-xs"
                        onClick={() => { setEditingRule(rule); setSheetOpen(true); }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-xs"
                        onClick={() => handleTest(rule)}
                        disabled={testingId === rule.id}
                      >
                        <Play className="w-3 h-3 mr-1.5" />
                        Test
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border" />
                      <DropdownMenuItem
                        className="text-xs text-loss"
                        onClick={() => setDeleteConfirm(rule)}
                      >
                        <Trash2 className="w-3 h-3 mr-1.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PortfolioRuleSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditingRule(null); }}
        onSubmit={editingRule ? handleUpdate : handleCreate}
        editingRule={editingRule}
      />

      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}
        onConfirm={handleDelete}
        title="Delete Portfolio Rule"
        description={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
      />

      <TestResultDialog
        open={testResultOpen}
        onOpenChange={setTestResultOpen}
        result={testResult}
      />
    </div>
  );
}
